import { Preferences } from "@capacitor/preferences";

const NOTIFICATION_PREFERENCES_KEY = "notification_preferences";

/**
 * Default notification preferences.
 * All notifications are enabled by default.
 */
const DEFAULT_PREFERENCES = {
  taskReminders: {
    enabled: true,
    deadlineApproaching: true,
    overdue: true,
    scheduleStart: true,
  },
  engagement: {
    enabled: true,
    dailyAgenda: true,
    comeback: true,
  },
  quietHours: {
    enabled: false,
    start: "22:00",
    end: "07:00",
  },
};

/**
 * Retrieve the user's notification preferences from local storage.
 * Returns default preferences if none have been saved.
 */
export const getNotificationPreferences = async () => {
  try {
    const { value } = await Preferences.get({
      key: NOTIFICATION_PREFERENCES_KEY,
    });
    if (value) {
      const saved = JSON.parse(value);
      // Merge with defaults to handle any newly added preference keys
      return {
        taskReminders: {
          ...DEFAULT_PREFERENCES.taskReminders,
          ...saved.taskReminders,
        },
        engagement: { ...DEFAULT_PREFERENCES.engagement, ...saved.engagement },
        quietHours: { ...DEFAULT_PREFERENCES.quietHours, ...saved.quietHours },
      };
    }
    return { ...DEFAULT_PREFERENCES };
  } catch (error) {
    console.warn("Failed to get notification preferences:", error.message);
    return { ...DEFAULT_PREFERENCES };
  }
};

/**
 * Save the user's notification preferences to local storage.
 */
export const setNotificationPreferences = async (prefs) => {
  try {
    await Preferences.set({
      key: NOTIFICATION_PREFERENCES_KEY,
      value: JSON.stringify(prefs),
    });
  } catch (error) {
    console.warn("Failed to save notification preferences:", error.message);
  }
};

/**
 * Check whether a specific notification type is allowed based on user preferences.
 *
 * @param {object} prefs - The full notification preferences object
 * @param {"taskReminders"|"engagement"} category - The top-level category
 * @param {string} subcategory - The specific notification type within the category
 * @returns {boolean}
 */
export const isNotificationTypeEnabled = (prefs, category, subcategory) => {
  const categoryPrefs = prefs[category];
  if (!categoryPrefs || !categoryPrefs.enabled) return false;
  return !(subcategory && categoryPrefs[subcategory] === false);
};

/**
 * Check if a given Date falls within the user's quiet hours.
 * Handles ranges that span midnight (e.g. 22:00–07:00).
 *
 * @param {Date} date - The date/time to check
 * @param {object} quietHours - The quiet hours config { enabled, start, end }
 * @returns {boolean}
 */
export const isWithinQuietHours = (date, quietHours) => {
  if (!quietHours || !quietHours.enabled) return false;

  const [startHour, startMin] = quietHours.start.split(":").map(Number);
  const [endHour, endMin] = quietHours.end.split(":").map(Number);

  const timeInMinutes = date.getHours() * 60 + date.getMinutes();
  const startInMinutes = startHour * 60 + startMin;
  const endInMinutes = endHour * 60 + endMin;

  if (startInMinutes <= endInMinutes) {
    // Same-day range (e.g. 08:00–17:00)
    return timeInMinutes >= startInMinutes && timeInMinutes < endInMinutes;
  } else {
    // Overnight range (e.g. 22:00–07:00)
    return timeInMinutes >= startInMinutes || timeInMinutes < endInMinutes;
  }
};

/**
 * Shift a notification time out of quiet hours.
 * If the time falls within quiet hours, returns the end of quiet hours
 * on the same day (or next day if the range spans midnight).
 *
 * @param {Date} date - The originally intended notification time
 * @param {object} quietHours - The quiet hours config { enabled, start, end }
 * @returns {Date} - The adjusted time (or original if not in quiet hours)
 */
export const shiftOutOfQuietHours = (date, quietHours) => {
  if (!isWithinQuietHours(date, quietHours)) return date;

  const [endHour, endMin] = quietHours.end.split(":").map(Number);
  const adjusted = new Date(date);
  adjusted.setHours(endHour, endMin, 0, 0);

  // If adjusted time is before the original, it means we need to go to the next day
  if (adjusted.getTime() <= date.getTime()) {
    adjusted.setDate(adjusted.getDate() + 1);
  }

  return adjusted;
};

export { DEFAULT_PREFERENCES };
